import { type StackProps } from '@mui/material'
import { Children, type ForwardedRef, forwardRef } from 'react'
import { compact } from 'lodash-es'
import { mergeSx } from 'lib/helpers/mergeSx/mergeSx'
import { sectionItemClasses, FIXED_WIDTH_ITEM_MAX_WIDTH } from 'settle-ui/components/SectionItem/SectionItem.consts'
import * as $ from './SectionItem.styled'

type Gutter = 'none' | 'small' | 'normal'

export interface SectionItemProps extends StackProps {
  fullWidth?: boolean
  horizontalGutter?: Gutter
  verticalGutter?: Gutter
  /**
   * When children are arranged horizontally (`flexDirection: 'row'`) and one child grows taller
   * than the others — e.g., a multi-select Autocomplete with wrapped chips next to a single-line
   * TextField — stretch all children (and their MUI input boxes) to the same height. Floating
   * labels stay top-pinned and input values stay top-aligned so the natural label→value reading
   * order is preserved across both fields.
   *
   * Has no effect on column layouts.
   */
  matchFieldHeights?: boolean
}
/**
 * A container component designed to wrap individual items within a Section or CollapsibleSection. It provides consistent spacing, width control, and layout behavior for section content.
 * Use cases
 * - Wrapping form fields or input groups within a section
 * - Creating consistent spacing between content blocks
 * - Building layouts that adapt between full-width and centered fixed-width designs
 * - Organizing content with standardized padding and margins
 */
export const SectionItem = forwardRef((props: SectionItemProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    children,
    verticalGutter = 'small',
    horizontalGutter = 'normal',
    fullWidth = false,
    matchFieldHeights = false,
    sx,
    className,
    ...restProps
  } = props

  if (Children.count(children) === 0) {
    return null
  }

  return (
    <$.SectionItem
      sx={mergeSx(
        {
          width: '100%',
          ...(verticalGutter === 'small' && { pt: 1, pb: 1 }),
          ...(verticalGutter === 'normal' && { pt: 2, pb: 2 }),
          ...(horizontalGutter === 'small' && { pl: 1, pr: 1 }),
          ...(horizontalGutter === 'normal' && { pl: 2, pr: 2 }),
          ...(!fullWidth && {
            maxWidth: `var(${FIXED_WIDTH_ITEM_MAX_WIDTH})`,
          }),
          ...(matchFieldHeights && {
            alignItems: 'stretch',
            '& > *': { display: 'flex', flexDirection: 'column' },
            '& .MuiFormControl-root': { flex: 1 },
            '& .MuiInputBase-root': { flex: 1, alignItems: 'flex-start' },
          }),
        },
        sx,
      )}
      className={compact([className, fullWidth ? sectionItemClasses.fullWidth : sectionItemClasses.fixedWidth]).join(
        ' ',
      )}
      ref={ref}
      {...restProps}
    >
      {children}
    </$.SectionItem>
  )
})

SectionItem.displayName = 'SectionItem'
